import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener('click', this.#handleClick);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Click handler.
   *
   * Quantity +/- buttons are handled entirely by cart-quantity-selector-component
   * (they use on:click="/decreaseQuantity" and on:click="/increaseQuantity").
   * Do NOT intercept them here — just let them bubble naturally.
   *
   * We only need to handle two things:
   *   1. Remove buttons  (on:click="/onLineItemRemove/<line>")
   *   2. Checkout button (navigation)
   *
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    // ── Checkout ─────────────────────────────────────────────────────────────
    // The checkout button is a plain <a href="/checkout"> — allow default
    // navigation but give the theme a chance to cancel via custom event.
    const checkoutBtn = target.closest(
      'a[href*="/checkout"], [data-checkout], .cart__checkout-button'
    );
    if (checkoutBtn) {
      const proceed = this.dispatchEvent(
        new CustomEvent('cart-drawer:checkout', { bubbles: true, cancelable: true })
      );
      if (!proceed) event.preventDefault();
      return;
    }

    // ── Remove item ───────────────────────────────────────────────────────────
    // Liquid renders: on:click="/onLineItemRemove/{{ item.index | plus: 1 }}"
    // The Component base class does NOT auto-wire on:click handlers that live
    // inside a *different* custom element (cart-items-component), so we must
    // delegate manually here.
    const actionBtn = target.closest('[on\\:click]');
    if (!actionBtn) return;

    const handler = actionBtn.getAttribute('on:click') || '';
    const removeMatch = handler.match(/^\/onLineItemRemove\/(\d+)$/);
    if (removeMatch) {
      event.preventDefault();
      const line = parseInt(removeMatch[1], 10);
      this.#getCartItemsComponent()?.onLineItemRemove(line);
    }
  };

  /** @returns {any} */
  #getCartItemsComponent() {
    return this.querySelector('cart-items-component');
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  open() {
    this.showDialog();
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  // ---------------------------------------------------------------------------
  // Sticky summary
  // ---------------------------------------------------------------------------

  #updateStickyState = () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');
    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }
    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
